// ===================================================
// ФАЙЛ: frontend/src/pages/Auth/AuthCheckPage.jsx
// Страница для проверки авторизации через iframe + postMessage
//
// НАЗНАЧЕНИЕ:
// Эта страница загружается в скрытом iframe на app.markbase.ru когда пользователь
// находится на markbase.ru и нужно проверить авторизован ли он на app поддомене.
//
// ПРОБЛЕМА: localStorage изолирован между доменами, поэтому токен на app.markbase.ru
// недоступен напрямую с markbase.ru.
//
// РЕШЕНИЕ: Создаем скрытый iframe на app поддомене, который:
// 1. Читает токен из своего localStorage (где он доступен)
// 2. Делает запрос к API для получения данных пользователя
// 3. Отправляет результат обратно на markbase.ru через postMessage
//
// Используется только для проверки авторизации с публичного домена.
// ===================================================
import { useEffect } from 'react';
import axios from '../../utils/axios';
import { API_ENDPOINTS } from '../../utils/constants';

const AuthCheckPage = () => {
  useEffect(() => {
    const checkAuth = async () => {
      // ✅ БЕЗОПАСНОСТЬ: Функция для безопасной валидации origin
      const validateOrigin = (origin) => {
        if (!origin || typeof origin !== 'string') {
          return '*';
        }
        try {
          const originUrl = new URL(origin);
          const hostname = originUrl.hostname;
          // ✅ БЕЗОПАСНОСТЬ: Разрешаем только markbase.ru и его поддомены (кроме app.*)
          if (hostname === 'markbase.ru' || hostname === 'www.markbase.ru' ||
              (hostname.endsWith('.markbase.ru') && !hostname.startsWith('app.'))) {
            // ✅ БЕЗОПАСНОСТЬ: Проверяем протокол (только https/http)
            if (originUrl.protocol === 'https:' || originUrl.protocol === 'http:') {
              return origin;
            }
          }
        } catch (_) {
          // ✅ БЕЗОПАСНОСТЬ: Никаких логов - просто возвращаем '*' при ошибке
        }
        return '*';
      };

      // ✅ БЕЗОПАСНОСТЬ: Функция для безопасной отправки сообщения
      const sendMessage = (success, payload, origin) => {
        try {
          if (window.parent) {
            window.parent.postMessage({
              type: 'AUTH_CHECK_RESPONSE',
              success: success === true, // ✅ БЕЗОПАСНОСТЬ: Принудительно boolean
              // ✅ ВАЖНО: PublicHeader ожидает поле `data`
              // payload сюда передаём в одном из форматов:
              // - полный ответ API: { success: true, data: { user, company } }
              // - или сразу { user, company }
              data: success && payload && typeof payload === 'object' ? payload : null
            }, origin);
          }
        } catch (_) {
          // ✅ БЕЗОПАСНОСТЬ: Никаких логов ошибок отправки
        }
      };

      try {
        // ✅ БЕЗОПАСНОСТЬ: Получаем origin из query параметров с валидацией
        const urlParams = new URLSearchParams(window.location.search);
        const targetOrigin = urlParams.get('origin');
        const allowedOrigin = validateOrigin(targetOrigin);

        // ✅ Канонический контур авторизации — UAM httpOnly cookie.
        // Наличие/отсутствие legacy localStorage token не является признаком
        // аутентификации, поэтому всегда проверяем реальную серверную сессию через /me.
        try {
          const response = await axios.get(API_ENDPOINTS.ME);

          // ✅ БЕЗОПАСНОСТЬ: Валидация структуры ответа перед отправкой
          // ✅ ИСПРАВЛЕНО: API возвращает {success: true, data: {user: {...}, company: {...}}}
          // Отправляем весь response.data, чтобы PublicHeader мог правильно обработать структуру
          if (response?.data?.success && response?.data?.data && typeof response.data.data === 'object') {
            // ✅ БЕЗОПАСНОСТЬ: Проверяем что userData содержит валидные данные
            const userData = response.data.data;
            if (userData.user && typeof userData.user === 'object' && !Array.isArray(userData.user)) {
              // ✅ Отправляем полный ответ от API (response.data), который содержит {success: true, data: {...}}
              sendMessage(true, response.data, allowedOrigin);
            } else {
              sendMessage(false, null, allowedOrigin);
            }
          } else {
            // Не авторизован или невалидный ответ
            sendMessage(false, null, allowedOrigin);
          }
        } catch (_) {
          // ✅ БЕЗОПАСНОСТЬ: Ошибка запроса - считаем что не авторизован, без логирования
          sendMessage(false, null, allowedOrigin);
        }
      } catch (_) {
        // ✅ БЕЗОПАСНОСТЬ: Критическая ошибка - никаких логов, отправляем что не авторизован
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const targetOrigin = urlParams.get('origin');
          const allowedOrigin = validateOrigin(targetOrigin);
          sendMessage(false, null, allowedOrigin);
        } catch (__) {
          // ✅ БЕЗОПАСНОСТЬ: Если даже это не удалось - молча игнорируем
        }
      }
    };

    checkAuth();
  }, []);

  // ✅ Эта страница не должна ничего отображать - она работает через iframe
  return null;
};

export default AuthCheckPage;

